import { useAuthStore } from '../stores/auth.js'
import { useCatalogStore } from '../stores/catalog.js'
import tokenService from '../utils/tokenService.js'
import logger from '../utils/logger.js'

/**
 * Camino ÚNICO de guardado de entradas de tiempo.
 * Unifica los 3 caminos dispersos del front viejo (DetailView insert/update/remove,
 * HomeView insertProject, SemanaDetailView). Toda creación/edición/borrado pasa por acá.
 *
 * El guardado real se hace en un solo lote (`saveBatch`, ver POSTTimesBatch.mjs)
 * en vez de una request HTTP por fila - antes `usePeriodDraft.js` hacía un
 * `await` secuencial por cada fila tocada, lento con muchas filas y con un
 * refresco de cache redundante por cada una.
 *
 * Contrato del backend (NO cambia respecto al guardado fila por fila que
 * reemplaza, solo se manda todo junto):
 *  - POST Times/batch { usercognito, userId, entries: [{ action: 'add'|'update'|'delete', ... }] }
 *    - add:    { fecha, projectid, tipo, tareaid, tipoImputacion, presupuestoItemId, tareaNoPresupuestadaId, desc, property1, property2, minutos }
 *    - update: { tsId, tareaId, tipoImputacion, presupuestoItemId, tareaNoPresupuestadaId, minutos, desc, property1, property2 }
 *    - delete: { tsId }
 *  Responde { success, data: { resultados: [{index, success, error?}] } },
 *  un resultado por entrada, en el mismo orden que se mandaron.
 *
 * El tiempo (`minutos`) se envía como "HH:MM".
 */
export function useTimeEntries() {
  const authStore = useAuthStore()
  const catalogStore = useCatalogStore()

  const flag = (v) => (v ? 1 : 0)

  /**
   * Guarda un lote de cambios del working copy (altas/ediciones/bajas) en
   * una sola llamada al backend.
   * @param {Array} cambios - entradas con _new/_dirty/_deleted (shape de usePeriodDraft.js)
   * @returns {Promise<Array<{index:number, success:boolean, error?:string}>>}
   *   resultado por entrada, en el mismo orden que `cambios`.
   */
  async function saveBatch(cambios) {
    const entries = cambios.map((e) => {
      if (e._deleted) {
        return { action: 'delete', tsId: e.ts_Id }
      }

      // D2: e.tareaId puede ser un ID sintético ("pi_<id>"/"tn_<id>") si se
      // eligió un ítem de presupuesto o una tarea no presupuestada - se
      // decodifica a los campos que espera el backend (ver POSTTimesBatch.mjs).
      const { tareaid, tipoImputacion, presupuestoItemId, tareaNoPresupuestadaId } = catalogStore.decodeTareaId(e.tareaId)

      if (e._new) {
        return {
          action: 'add',
          fecha: e.ts_Fecha,
          projectid: e.ts_ProjectId,
          tipo: e.ts_Tipo,
          tareaid,
          tipoImputacion,
          presupuestoItemId,
          tareaNoPresupuestadaId,
          desc: e.tareadesc ?? '',
          property1: flag(e.property1),
          property2: flag(e.property2),
          minutos: e.tiempo ?? '00:00'
        }
      }

      return {
        action: 'update',
        tsId: e.ts_Id,
        tareaId: tareaid,
        tipoImputacion,
        presupuestoItemId,
        tareaNoPresupuestadaId,
        minutos: e.tiempo ?? '00:00',
        desc: e.tareadesc ?? '',
        property1: flag(e.property1),
        property2: flag(e.property2)
      }
    })

    const body = {
      usercognito: authStore.usuario,
      userId: authStore.userId,
      entries
    }

    let res
    try {
      res = await tokenService.post('Times/batch', body)
    } catch (err) {
      logger.error('saveBatch falló:', err)
      throw err
    }
    if (res?.success === false) {
      const err = new Error(res.error?.message || 'No se pudo guardar el lote de horas')
      logger.error('saveBatch rechazado por el backend:', err)
      throw err
    }

    return res?.data?.resultados || []
  }

  return { saveBatch }
}
